import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useToast } from '../composables/useToast'
import { supabase } from '../lib/supabase'
import type { Document, Factuur, FactuurHerinnering, FactuurProduct, FactuurReminderScheduleItem, Ibedrijf } from '../../../../types'

type FactuurFilters = {
  status?: string | null
  query?: string | null
}

type FactuurUser = {
  id: string
  email: string | null
  display_name: string | null
}

type FactuurRow = {
  id: string
  ibedrijf_id: string
  opdrachtgever_id: string | null
  factuurnr: string | null
  factuur_datum: string | null
  vervaltermijn_dagen: number | null
  vervaldatum: string | null
  status: string
  status_history: unknown
  factuur_adres_id: string | null
  opmerkingen: string | null
  betaald_datum: string | null
  betaald_bedrag: number | null
  payment_link: string | null
  rapport_na_betaling: boolean | null
  reminder_overrides: unknown
  subtotaal: number | null
  btw_totaal: number | null
  totaal: number | null
  created_at: string | null
  updated_at: string | null
  opdrachtgever?: FactuurUser | null
  ibedrijf?: (Partial<Ibedrijf> & {
    users?: { display_name?: string | null; email?: string | null } | null
  }) | null
  factuur_producten?: Array<FactuurProduct> | null
  factuur_herinneringen?: Array<FactuurHerinnering> | null
}

const LIST_SELECT = `
  id,
  ibedrijf_id,
  opdrachtgever_id,
  factuurnr,
  factuur_datum,
  vervaltermijn_dagen,
  vervaldatum,
  status,
  status_history,
  factuur_adres_id,
  opmerkingen,
  betaald_datum,
  betaald_bedrag,
  payment_link,
  rapport_na_betaling,
  reminder_overrides,
  subtotaal,
  btw_totaal,
  totaal,
  created_at,
  updated_at,
  opdrachtgever:opdrachtgever_id(id, email, display_name),
  ibedrijf:ibedrijf_id(id, contactnaam, users(display_name, email)),
  factuur_producten(
    id,
    factuur_id,
    volgorde,
    omschrijving,
    aantal,
    prijs,
    btw_rate,
    source_offerte_id,
    source_offerte_product_id,
    source_bundel_id,
    source_rapport_id,
    source_herinspectie_id,
    created_at,
    updated_at
  )
`

const DETAIL_SELECT = `
  ${LIST_SELECT},
  factuur_herinneringen(
    id,
    factuur_id,
    level,
    scheduled_at,
    sent_at,
    is_last,
    status,
    error,
    created_at
  )
`

function normalizeReminderOverrides(value: unknown): FactuurReminderScheduleItem[] | null {
  return Array.isArray(value) ? (value as FactuurReminderScheduleItem[]) : null
}

function normalizeStatusHistory(value: unknown) {
  return Array.isArray(value) ? value : []
}

function normalizeProduct(product: FactuurProduct): FactuurProduct {
  const amount = Number(product.aantal || 0)
  const price = Number(product.prijs || 0)
  const btwRate = Number(product.btw_rate || 0)
  const regelTotaal = amount * price
  const regelBtw = regelTotaal * (btwRate / 100)

  return {
    ...product,
    regel_totaal: regelTotaal,
    regel_btw: regelBtw,
    regel_totaal_incl_btw: regelTotaal + regelBtw,
  }
}

function normalizeFactuur(row: FactuurRow | null): Factuur | null {
  if (!row) return null

  return {
    ...row,
    status: row.status as Factuur['status'],
    status_history: normalizeStatusHistory(row.status_history) as Factuur['status_history'],
    reminder_overrides: normalizeReminderOverrides(row.reminder_overrides),
    producten: Array.isArray(row.factuur_producten)
      ? row.factuur_producten.slice().sort((left, right) => left.volgorde - right.volgorde).map(normalizeProduct)
      : [],
    herinneringen: Array.isArray(row.factuur_herinneringen) ? row.factuur_herinneringen : [],
    opdrachtgever: row.opdrachtgever ?? null,
    ibedrijf: row.ibedrijf ?? null,
  } as Factuur
}

export const useFacturenStore = defineStore('facturen', () => {
  const toast = useToast()

  const facturen = ref<Factuur[]>([])
  const currentFactuur = ref<Factuur | null>(null)
  const currentDocument = ref<Document | null>(null)
  const loading = ref(false)
  const downloading = ref(false)
  const error = ref<string | null>(null)

  const hasFacturen = computed(() => facturen.value.length > 0)

  async function fetchFacturen(filters: FactuurFilters = {}) {
    loading.value = true
    error.value = null

    try {
      let query = (supabase.from('facturen') as any).select(LIST_SELECT).order('created_at', { ascending: false })

      if (filters.status) {
        query = query.eq('status', filters.status)
      }

      const trimmedQuery = filters.query?.trim()
      if (trimmedQuery) {
        // SECURITY: supabase-js does NOT escape the contents of `.or()` — the
        // argument is a raw PostgREST filter expression. Interpolating the user's
        // search term directly lets metacharacters like `,` `.` `(` `)` inject
        // extra filter clauses. Wrapping the value in double quotes makes
        // PostgREST treat those reserved characters literally; inside quotes only
        // `"` and `\` are special, so those are backslash-escaped here. Length is
        // capped to keep the generated filter bounded. Behavior is unchanged: a
        // case-insensitive substring match across factuurnr + opmerkingen.
        const safeTerm = trimmedQuery.slice(0, 100).replace(/[\\"]/g, '\\$&')
        query = query.or(`factuurnr.ilike."%${safeTerm}%",opmerkingen.ilike."%${safeTerm}%"`)
      }

      const { data, error: queryError } = await query
      if (queryError) throw queryError

      facturen.value = ((data || []) as unknown as FactuurRow[]).map(normalizeFactuur).filter(Boolean) as Factuur[]
      return facturen.value
    } catch (err) {
      console.error('[facturen] fetchFacturen error', err)
      error.value = err instanceof Error ? err.message : 'Fout bij ophalen van facturen'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }

  async function fetchFactuurById(id: string) {
    loading.value = true
    error.value = null

    try {
      const { data, error: queryError } = await (supabase.from('facturen') as any).select(DETAIL_SELECT).eq('id', id).maybeSingle()
      if (queryError) throw queryError

      const normalized = normalizeFactuur((data as unknown as FactuurRow | null) ?? null)
      currentFactuur.value = normalized
      return normalized
    } catch (err) {
      console.error('[facturen] fetchFactuurById error', err)
      error.value = err instanceof Error ? err.message : 'Fout bij ophalen van factuur'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }

  async function fetchFactuurDocument(factuurId: string) {
    const { data, error: queryError } = await supabase
      .from('documents')
      .select('*')
      .eq('type', 'factuur')
      .eq('factuur_id', factuurId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (queryError) throw queryError

    currentDocument.value = (data as Document | null) ?? null
    return currentDocument.value
  }

  async function getFactuurPdfSignedUrl(factuurId: string, downloadFileName?: string) {
    downloading.value = true

    try {
      const document = await fetchFactuurDocument(factuurId)
      if (!document?.pdf_path) {
        return null
      }

      const { data, error: signedUrlError } = await supabase.storage
        .from('generated-docs')
        .createSignedUrl(document.pdf_path, 3600, downloadFileName ? { download: downloadFileName } : undefined)
      if (signedUrlError) throw signedUrlError

      return data?.signedUrl || null
    } catch (err) {
      console.error('[facturen] getFactuurPdfSignedUrl error', err)
      error.value = err instanceof Error ? err.message : 'Kon PDF niet ophalen'
      throw err
    } finally {
      downloading.value = false
    }
  }

  return {
    facturen,
    currentFactuur,
    currentDocument,
    loading,
    downloading,
    error,
    hasFacturen,
    fetchFacturen,
    fetchFactuurById,
    fetchFactuurDocument,
    getFactuurPdfSignedUrl,
  }
})
